import React, { useState } from 'react';
import axios from 'axios';
import {
  FIELD_TYPES,
  createEmptyFormField,
  getFieldTypeLabel,
  serializeFormFieldsForApi,
  normalizeFormFieldsFromApi
} from '../../utils/registrationFormUtils';

const editableTypes = FIELD_TYPES.filter((type) => type.value !== 'forum');

const RegistrationFormEditor = ({ fields, setFields, onSuccess }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const updateField = (index, updater) => {
    setFields((prev) => prev.map((field, fieldIndex) => (
      fieldIndex === index
        ? (typeof updater === 'function' ? updater(field) : { ...field, ...updater })
        : field
    )));
  };

  const handleAddField = (type) => {
    setFields((prev) => [...prev, createEmptyFormField(type)]);
  };

  const handleDeleteField = (index) => {
    const field = fields[index];
    if (field.locked) {
      alert('Это поле нельзя удалить — оно связывает заявку с программой.');
      return;
    }

    if (!window.confirm(`Удалить поле «${field.label || 'без названия'}»?`)) {
      return;
    }

    setFields((prev) => prev.filter((_, fieldIndex) => fieldIndex !== index));
  };

  const moveField = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= fields.length || fromIndex === toIndex) {
      return;
    }

    setFields((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const handleDragStart = (event, index) => {
    setDragIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDrop = (event, dropIndex) => {
    event.preventDefault();
    const fromIndex = dragIndex ?? Number(event.dataTransfer.getData('text/plain'));
    moveField(fromIndex, dropIndex);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleTypeChange = (index, type) => {
    updateField(index, (field) => ({
      ...field,
      type,
      options: type === 'select'
        ? (field.options?.length ? field.options : ['Вариант 1'])
        : [],
      placeholder: type === 'select'
        ? (field.placeholder || 'Выберите значение')
        : type === 'phone'
          ? '+7 (___) ___-__-__'
          : type === 'url'
            ? 'https://'
            : field.placeholder
    }));
  };

  const handleOptionChange = (fieldIndex, optionIndex, value) => {
    updateField(fieldIndex, (field) => {
      const options = [...(field.options || [])];
      options[optionIndex] = value;
      return { ...field, options };
    });
  };

  const handleAddOption = (fieldIndex) => {
    updateField(fieldIndex, (field) => ({
      ...field,
      options: [...(field.options || []), `Вариант ${(field.options?.length || 0) + 1}`]
    }));
  };

  const handleDeleteOption = (fieldIndex, optionIndex) => {
    updateField(fieldIndex, (field) => ({
      ...field,
      options: (field.options || []).filter((_, index) => index !== optionIndex)
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (isSaving) return;

    const emptySelect = fields.find((field) => (
      field.type === 'select' && !(field.options || []).some((option) => String(option).trim())
    ));

    if (emptySelect) {
      alert(`У списка «${emptySelect.label || 'без названия'}» нет вариантов`);
      return;
    }

    setIsSaving(true);
    try {
      const response = await axios.put('/api/registration-form', {
        fields: serializeFormFieldsForApi(fields)
      });
      const savedFields = normalizeFormFieldsFromApi(response.data?.fields || []);
      setFields(savedFields);
      onSuccess('Форма регистрации сохранена', savedFields);
    } catch (error) {
      console.error('Error saving registration form:', error);
      alert(error.response?.data?.message || 'Ошибка при сохранении формы');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form className="registration-form-editor" onSubmit={handleSave}>
      <div className="program-editor-toolbar">
        <p className="program-editor-hint">
          Перетащите поля, чтобы изменить порядок на форме. Чекбокс согласия всегда остаётся внизу.
        </p>
      </div>

      {fields.length === 0 ? (
        <div className="admin-empty-state admin-empty-state--compact">
          <p>Полей пока нет</p>
        </div>
      ) : (
        fields.map((field, index) => (
          <div
            key={field.clientKey || field.key}
            className={`table-row form-field-row ${dragOverIndex === index ? 'is-drag-over' : ''} ${field.locked ? 'is-locked' : ''}`}
            onDragOver={(event) => handleDragOver(event, index)}
            onDragLeave={() => setDragOverIndex(null)}
            onDrop={(event) => handleDrop(event, index)}
          >
            <div
              className="table-row__number form-field-row__handle"
              title="Перетащить"
              draggable
              onDragStart={(event) => handleDragStart(event, index)}
              onDragEnd={handleDragEnd}
            >
              ⋮⋮
            </div>
            <div className="table-row__fields form-field-row__fields">
              <div className="admin-field">
                <label>Название</label>
                <input
                  type="text"
                  value={field.label}
                  onChange={(event) => updateField(index, { label: event.target.value })}
                  placeholder="Как поле видно на форме"
                />
              </div>
              <div className="admin-field">
                <label>Тип</label>
                {field.locked ? (
                  <input type="text" value={getFieldTypeLabel(field.type)} disabled />
                ) : (
                  <select
                    value={field.type}
                    onChange={(event) => handleTypeChange(index, event.target.value)}
                  >
                    {editableTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="admin-field">
                <label>Подсказка</label>
                <input
                  type="text"
                  value={field.placeholder}
                  onChange={(event) => updateField(index, { placeholder: event.target.value })}
                  placeholder={field.type === 'select' || field.type === 'forum' ? 'Текст в списке' : 'Необязательно'}
                />
              </div>
              <label className="form-field-required">
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={(event) => updateField(index, { required: event.target.checked })}
                />
                Обязательное
              </label>
              {field.locked && (
                <p className="form-field-locked-note">Системное поле: связывает заявку с программой</p>
              )}
              {field.type === 'select' && (
                <div className="form-field-options">
                  <div className="form-field-options__header">
                    <span>Варианты списка</span>
                    <button
                      type="button"
                      className="admin-action-btn admin-action-btn--secondary"
                      onClick={() => handleAddOption(index)}
                    >
                      + Вариант
                    </button>
                  </div>
                  {(field.options || []).map((option, optionIndex) => (
                    <div key={`${field.clientKey}-option-${optionIndex}`} className="form-field-option">
                      <input
                        type="text"
                        value={option}
                        onChange={(event) => handleOptionChange(index, optionIndex, event.target.value)}
                        placeholder={`Вариант ${optionIndex + 1}`}
                      />
                      <button
                        type="button"
                        className="row-action-btn row-action-btn--danger"
                        onClick={() => handleDeleteOption(index, optionIndex)}
                        disabled={(field.options || []).length <= 1}
                      >
                        Удалить
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="row-actions">
              <button
                type="button"
                className="row-action-btn row-action-btn--select"
                onClick={() => moveField(index, index - 1)}
                disabled={index === 0}
              >
                Вверх
              </button>
              <button
                type="button"
                className="row-action-btn row-action-btn--select"
                onClick={() => moveField(index, index + 1)}
                disabled={index === fields.length - 1}
              >
                Вниз
              </button>
              <button
                type="button"
                className="row-action-btn row-action-btn--danger"
                onClick={() => handleDeleteField(index)}
                disabled={field.locked}
              >
                Удалить
              </button>
            </div>
          </div>
        ))
      )}

      <div className="admin-form-footer">
        <button type="button" className="admin-action-btn admin-action-btn--secondary" onClick={() => handleAddField('text')}>
          + Текстовое поле
        </button>
        <button type="button" className="admin-action-btn admin-action-btn--secondary" onClick={() => handleAddField('select')}>
          + Список
        </button>
        <button type="button" className="admin-action-btn admin-action-btn--secondary" onClick={() => handleAddField('phone')}>
          + Телефон
        </button>
        <button type="submit" className="admin-action-btn admin-action-btn--primary" disabled={isSaving}>
          {isSaving ? 'Сохранение...' : 'Сохранить форму'}
        </button>
      </div>
    </form>
  );
};

export default RegistrationFormEditor;
